(() => {
  'use strict';
  if (customElements.get('worth-it-decision')) return;
  class WorthItDecision extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const controller = new AbortController();
      this.cleanup = () => { controller.abort(); this.resizeObserver?.disconnect(); this.quantityObserver?.disconnect(); cancelAnimationFrame(this.photoFrame); clearTimeout(this.billRestoreTimer); this.billRestoreTimer = undefined; };
      const options = { signal: controller.signal };
      const money = value => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value / 100);
      this.querySelectorAll('[data-wi-choice-group]').forEach(group => {
        const buttons = [...group.querySelectorAll('[data-wi-choice]')];
        const show = button => {
          buttons.forEach(item => {
            const active = item === button;
            item.setAttribute('aria-pressed', String(active));
            const panel = this.querySelector(`#${CSS.escape(item.getAttribute('aria-controls'))}`);
            if (panel) panel.hidden = !active;
          });
        };
        buttons.forEach(button => button.addEventListener('click', () => show(button), options));
        if (buttons.length) show(buttons[0]);
      });
      this.querySelectorAll('[data-wi-enhanced]').forEach(element => { element.hidden = false; });
      this.querySelectorAll('[data-wi-setup-guide]').forEach(guide => {
        const choice = guide.querySelector('[data-wi-setup-choice]');
        const response = guide.querySelector('[data-wi-setup-response]');
        const answers = [...guide.querySelectorAll('[data-wi-setup-answer]')];
        if (!choice || !response || answers.length !== 2) return;
        choice.hidden = false;
        answers.forEach(button => button.addEventListener('click', () => {
          answers.forEach(answer => answer.setAttribute('aria-pressed', String(answer === button)));
          response.textContent = button.dataset.wiSetupAnswer === 'checked'
            ? 'Next, review the listed contents. Contact us if any fit or product detail is unclear.'
            : 'Use “Ask about my setup” below. Include the product name and the detail you need us to confirm.';
        }, options));
      });
      const main = this.closest('product-info');
      const bill = this.querySelector('[data-wi-bill]');
      if (bill && main) {
        const getQuantityInput = () => {
          const fallback = main.querySelector('[data-wi-native-product]');
          if (fallback && !fallback.hidden) return fallback.querySelector('input[name="quantity"]');
          if (!main.hasAttribute('data-wi-product-ready')) return null;
          const enhanced = main.querySelector('product-form[data-wi-enhanced-product]');
          if (!enhanced || enhanced.hidden) return null;
          const form = enhanced.querySelector('form');
          const input = form?.elements?.namedItem('quantity');
          return input && main.contains(input) && input.form === form ? input : null;
        };
        const updateBill = () => {
          const input = getQuantityInput();
          const id = input?.form?.querySelector('[name="id"]');
          const quantity = input ? Number(input.value) : NaN;
          const minimum = Number(input?.min || 1);
          const maximum = input?.hasAttribute('max') ? Number(input.max) : null;
          const step = Number(input?.step || 1);
          const price = Number(bill.dataset.wiUnitPrice);
          const plan = input?.form?.elements?.namedItem('selling_plan');
          const valid = !!(input && !input.disabled && input.validity.valid && id && !id.disabled &&
            /^\d+$/.test(bill.dataset.wiVariantId || '') && id.value === bill.dataset.wiVariantId &&
            Number.isSafeInteger(quantity) && quantity > 0 && Number.isSafeInteger(minimum) && minimum > 0 && quantity >= minimum &&
            Number.isSafeInteger(step) && step > 0 && (quantity - minimum) % step === 0 &&
            (maximum === null || Number.isSafeInteger(maximum) && quantity <= maximum) &&
            Number.isSafeInteger(price) && price > 0 && (!plan || typeof plan.value === 'string' && plan.value.trim() === '') &&
            !main.selectionUnconfirmed && !main.quantityRulesUnconfirmed && !main.quantityPriceBreaksConfigured);
          const output = bill.querySelector('[data-wi-bill-values]');
          output.hidden = !valid;
          bill.querySelector('[data-wi-bill-invalid]').hidden = valid;
          if (!valid) return;
          const items = quantity * price;
          if (!Number.isSafeInteger(items)) { output.hidden = true; bill.querySelector('[data-wi-bill-invalid]').hidden = false; return; }
          const shipping = items >= 99900 ? 0 : 4900;
          bill.querySelector('[data-wi-bill-quantity]').textContent = `${quantity} ${quantity === 1 ? 'pack' : 'packs'}`;
          bill.querySelector('[data-wi-bill-items]').textContent = money(items);
          bill.querySelector('[data-wi-bill-shipping]').textContent = shipping ? money(shipping) : 'Free';
          bill.querySelector('[data-wi-bill-total]').textContent = money(items + shipping);
          bill.querySelector('[data-wi-bill-gap]').textContent = shipping ? `${money(99900 - items)} below the free-shipping threshold for these items.` : 'These items reach the free-shipping threshold.';
          bill.querySelector('[data-wi-bill-progress]').style.width = `${Math.min(100, items / 99900 * 100)}%`;
        };
        main.addEventListener('input', event => { if (event.target.matches('input[name="quantity"]')) updateBill(); }, options);
        main.addEventListener('change', event => { if (event.target.matches('input[name="quantity"], [name="id"], [name="selling_plan"]')) updateBill(); }, options);
        main.addEventListener('worth-it:product-ready', updateBill, options);
        window.addEventListener('pageshow', () => {
          clearTimeout(this.billRestoreTimer);
          this.billRestoreTimer = setTimeout(() => {
            this.billRestoreTimer = undefined;
            if (this.isConnected && !controller.signal.aborted) updateBill();
          }, 0);
        }, options);
        this.quantityObserver = new MutationObserver(updateBill);
        this.quantityObserver.observe(main, { attributes: true, attributeFilter: ['data-wi-product-ready'] });
        const fallback = main.querySelector('[data-wi-native-product]');
        if (fallback) this.quantityObserver.observe(fallback, { attributes: true, attributeFilter: ['hidden'] });
        main.querySelectorAll('input[name="quantity"], input[name="id"]').forEach(input => {
          this.quantityObserver.observe(input, { attributes: true, attributeFilter: ['disabled', 'min', 'max', 'step', 'value'] });
        });
        this.querySelector('[data-wi-edit-quantity]')?.addEventListener('click', () => { const input = getQuantityInput(); input?.scrollIntoView({ block: 'center', behavior: 'auto' }); input?.focus({ preventScroll: true }); }, options);
        updateBill();
      }
      const compare = this.querySelector('[data-wi-compare]');
      const catalogueNode = this.querySelector('[data-wi-compare-data]');
      if (compare && catalogueNode) {
        try {
          const catalogue = JSON.parse(catalogueNode.textContent);
          const select = compare.querySelector('select');
          const card = compare.querySelector('[data-wi-compare-card]');
          const update = () => {
            const item = catalogue.find(product => String(product.id) === select.value);
            if (!item) return;
            card.querySelector('h4').textContent = item.title;
            card.querySelector('[data-wi-compare-price]').textContent = money(item.price);
            card.querySelector('[data-wi-compare-pack]').textContent = item.pack;
            const image = card.querySelector('img');
            if (item.image) { image.removeAttribute('srcset'); image.src = item.image; image.alt = item.title; image.hidden = false; } else image.hidden = true;
            card.querySelector('a').href = item.url;
            card.querySelector('[data-wi-compare-availability]').textContent = item.available ? 'Available to add' : 'Currently unavailable';
          };
          select.addEventListener('change', update, options);
          update();
        } catch (_) { compare.querySelector('[data-wi-compare-picker]').hidden = true; }
      }
      const rail = this.querySelector('[data-wi-photo-rail]');
      if (rail) {
        const photos = [...rail.querySelectorAll('figure')];
        if (!photos.length) { this.querySelector('.wi-decision__photo-tools').hidden = true; return; }
        const previous = this.querySelector('[data-wi-photo-previous]');
        const next = this.querySelector('[data-wi-photo-next]');
        let current = 0;
        const update = () => {
          const start = rail.getBoundingClientRect().left;
          let distance = Infinity;
          photos.forEach((photo, index) => { const value = Math.abs(photo.getBoundingClientRect().left - start); if (value < distance) { current = index; distance = value; } });
          previous.disabled = rail.scrollLeft <= 2;
          next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
          this.querySelector('[data-wi-photo-status]').textContent = `Photo ${current + 1} of ${photos.length}`;
        };
        const move = direction => {
          const target = Math.max(0, Math.min(photos.length - 1, current + direction));
          rail.scrollTo({ left: photos[target].offsetLeft - photos[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        };
        previous.addEventListener('click', () => move(-1), options);
        next.addEventListener('click', () => move(1), options);
        rail.addEventListener('scroll', () => { cancelAnimationFrame(this.photoFrame); this.photoFrame = requestAnimationFrame(update); }, { ...options, passive: true });
        if ('ResizeObserver' in window) { this.resizeObserver = new ResizeObserver(update); this.resizeObserver.observe(rail); }
        update();
      }
    }
    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
  }
  customElements.define('worth-it-decision', WorthItDecision);
})();
